import moment from "moment";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { useDebouncedCallback } from "use-debounce";

import deleteWhite from "../../../assets/img/icons/delete_white.svg";
import Info from "../../../assets/img/icons/info.svg";
import Plus from "../../../assets/img/icons/plus_white.svg";
import ComponentCircleLoader from "../../../components/Loaders/ComponentCircleLoader";
import NoDataComponent from "../../../components/NoDataComponent/NoDataComponent";
import CommonTooltip from "../../../components/Tooltip/CommonTooltip";
import { useScrollShadow } from "../../../customHooks/useScrollShadow";
import {
  setPathName,
  timesheetActivityGroupUrls,
} from "../../../filterReducer";
import {
  ButtonIcon,
  ColoredButtonWithIcon,
  WhiteButton,
} from "../../../styledComponents/buttons";
import {
  CommonGrid,
  CommonText,
  Container,
  ContainerNew,
  PageTitle,
} from "../../../styledComponents/common";
import { ActivityTextSection } from "../../../styledComponents/members";
import {
  CalendarContainer,
  DeleteButton,
  InfoButton,
  StickyFilterContainer,
  TimeSlot,
  TimeSlotCard,
  TimeSlotInnerCard,
  TimeSlotSmallInfo,
  TimeText,
  TimesheetTooltipContainer,
  UpperTotalHour,
} from "../../../styledComponents/timesheet";
import { toHHMMSS } from "../../../utils/helper";
import TimesheetFilter from "../TimesheetFilter";
import DeleteTimeModal from "./DeleteTimeModal";
import ManualTimeModal from "./ManualTimeModal";
import { TimeSlotContent } from "./TimeSlotContent";
import {
  getDuration,
  getDurationInMinutes,
  getHours,
  getZeroHourTime,
} from "./helperFunctions";
import { timeList } from "./timeList";

export const TimesheetDetailsPopup = ({
  index,
  data,
  startPosition,
  getDuration,
  toggleDeleteTimeModal,
  setInfoPopup,
  selectedOrganization,
}) => {
  const [hover, setHover] = useState(false);
  const popUpRef = useRef(null);

  //outside click function
  useOutsideClickHandler(popUpRef);
  function useOutsideClickHandler(popUpRef) {
    useEffect(() => {
      function handleClickOutside(event) {
        if (popUpRef.current && !popUpRef.current.contains(event.target)) {
          setInfoPopup(-1);
        }
      }
      // Bind the event listener
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        // Unbind the event listener on clean up
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [popUpRef]);
  }

  const onHoverChange = value => {
    setHover(value);
  };
  return (
    <TimeSlotCard
      index={index}
      top={data.top - 85}
      height={75}
      startPosition={startPosition}
      style={{
        left: "200px",
        zIndex: "5",
      }}
      ref={popUpRef}
      onMouseEnter={() => onHoverChange(true)}
      onMouseLeave={() => onHoverChange(false)}
    >
      <TimeSlotInnerCard>
        {hover && (
          <DeleteButton onClick={() => toggleDeleteTimeModal(data)}>
            <img
              src={deleteWhite}
              style={{ width: "16px", height: "16px" }}
              alt="delete"
            />
            <TimesheetTooltipContainer>
              <CommonTooltip
                tooltipText="Delete"
                left="-50px"
                top="-46px"
                width="85px"
                height="25px"
              />
            </TimesheetTooltipContainer>
          </DeleteButton>
        )}
        <ActivityTextSection style={{ textAlign: "left" }}>
          {selectedOrganization?.type !== "service" && <CommonText
            name
            fontSize="14px"
            fontWeight="600"
            style={{
              opacity: !data ? "auto" : !data.work_order ? "50%" : "auto",
            }}
          >
            {data.work_order
              ? data.work_order.name
              : `(No ${
                  (selectedOrganization?.type || "operation") === "service"
                    ? "job"
                    : "project"
                })`}
          </CommonText>}
          <CommonText
            fontSize={selectedOrganization?.type !== "service" ? "12px" : "14px"}
            fontWeight="600"
            style={{ opacity: !data ? "auto" : !data.job ? "50%" : "auto" }}
          >
            {data.job ? data.job.name : "(No task)"}
          </CommonText>
        </ActivityTextSection>
        <ActivityTextSection>
          <CommonText name fontSize="14px" fontWeight="600">
            {data.start_time.format("h:mm a")}
          </CommonText>
          <CommonText fontSize="12px" fontWeight="600">
            Start Time
          </CommonText>
        </ActivityTextSection>
        <ActivityTextSection>
          <CommonText name fontSize="14px" fontWeight="600">
            {data.end_time.format("h:mm a")}
          </CommonText>
          <CommonText fontSize="12px" fontWeight="600">
            End Time
          </CommonText>
        </ActivityTextSection>
        <ActivityTextSection style={{ textAlign: "right" }}>
          <CommonText name fontSize="14px" fontWeight="600">
            {getZeroHourTime(data.duration)}
          </CommonText>
          <CommonText fontSize="12px" fontWeight="600">
            Total Time
          </CommonText>
        </ActivityTextSection>
      </TimeSlotInnerCard>
    </TimeSlotCard>
  );
};

const Daily = ({
  selectedOrganization,
  getDailyTimesheets,
  timesheet,
  createDailyTimesheets,
  manualTimeAdded,
  manualTimeDetails,
  clearDailyTimesheetAdded,
  deleteDailyTimesheets,
  projectsList,
  getProjectAssigneeList,
  projectAssigneeList,
  projectTaskList,

  getOrganizationMembersList,
  orgMembersList,
  membersList,
  manualTimeLoading,
  deleteTimesheetLoading,
  isLoading,

  //member short list
  memberShortListIsLoading,
  orgMembersShortList,
  getOrganizationMembersShortList,

  //Office
  getOfficeDetails,
  officeDetails,
  getOfficesShortList,
  officesShortList,
  filters,
}) => {
  const showShadow = useScrollShadow(60);
  const { t } = useTranslation();
  const [manualTimeModalVisible, setManualTimeModalVisible] = useState(false);
  const [deleteTimesheetModalVisible, setDeleteTimesheetModalVisible] =
    useState(false);
  const [selectedDate, setSelectedDate] = useState(
    timesheetActivityGroupUrls.includes(filters?.pathname) &&
      filters?.selectedDate
      ? moment(filters.selectedDate).format("YYYY-MM-DD")
      : moment().format("YYYY-MM-DD"),
  );
  const isYesterday = moment(selectedDate).isSame(
    moment().subtract(1, "day"),
    "day",
  );

  // Dynamic labels based on organization type (default to "operation" if not defined)
  const orgType = selectedOrganization?.type || "operation";
  const isOperationType = orgType === "operation";
  const allProjectsLabel = isOperationType
    ? "(All Active Projects)"
    : "(All Active Jobs)";
  const allTasksLabel = isOperationType ? "(All Tasks)" : "(All Jobs)";

  const [selectedProject, setSelectedProject] = useState(
    timesheetActivityGroupUrls.includes(filters?.pathname) &&
      filters?.selectedProject
      ? filters?.selectedProject
      : { value: null, label: allProjectsLabel },
  );
  const [selectedTask, setSelectedTask] = useState(
    timesheetActivityGroupUrls.includes(filters?.pathname) &&
      filters?.selectedTask
      ? filters?.selectedTask
      : { value: "", label: allTasksLabel },
  );
  const [selectedMember, setSelectedMember] = useState(
    timesheetActivityGroupUrls.includes(filters?.pathname) &&
      filters?.selectedMember
      ? filters?.selectedMember
      : "",
  );
  const [timesheetList, setTimesheetList] = useState([]);
  const [durations, setDurations] = useState([]);
  const [selectIndex, setSelectIndex] = useState(-1);
  const [currentTimesheet, setCurrentTimesheet] = useState(null);
  const [infoPopup, setInfoPopup] = useState(-1);

  const [selectedTimezone, setSelectedTimezone] = useState(null);
  const [selectedOffice, setSelectedOffice] = useState(
    timesheetActivityGroupUrls.includes(filters?.pathname) &&
      filters?.selectedOffice
      ? filters?.selectedOffice
      : { value: "", label: "(All Teams)" },
  );
  const [isTodaySelected, setIsTodaySelected] = useState(false);
  const dispatch = useDispatch();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  window.history.replaceState(null, "", `${location.pathname}`);
  const queryParamMember = queryParams.get("member");
  const queryParamDate = queryParams.get("date");
  const queryParamOffice = queryParams.get("team");

  // Save filters to Redux when they change
  useEffect(() => {
    dispatch(
      setPathName({
        pathname: location.pathname,
        // selectedTask,
        // selectedProject,
        selectedMember,
        selectedDate,
        // selectedTimezone,
        selectedOffice,
      }),
    );
  }, [
    // selectedTask,
    // selectedProject,
    selectedMember,
    selectedDate,
    // selectedTimezone,
    selectedOffice,
    dispatch,
    location.pathname,
  ]);

  useEffect(() => {
    if (selectedOrganization?.id) {
      const parsed = moment(queryParamDate, "DD-MM-YYYY", true);
      const date = parsed.isValid()
        ? parsed.format("YYYY-MM-DD")
        : selectedDate;

      let payload = {
        organization_id: selectedOrganization.id,
        date: date,
        user_id: queryParamMember || "",
        office_id: queryParamOffice || "",
      };
      setSelectedDate(date);

      getDailyTimesheets(payload);
    }
  }, [selectedOrganization]);

  // Watch all filter states + selectedOrganization
  useEffect(() => {
    if (!selectedOrganization?.id) return;

    const payload = {
      organization_id: selectedOrganization.id,
      date: selectedDate,
      user_id: selectedMember?.id || "",
      office_id: selectedOffice?.value || "",
    };

    if (selectedTimezone?.timezone) {
      payload.timezone = selectedTimezone.timezone;
    }

    getDailyTimesheets(payload);
  }, [
    selectedOrganization?.id,
    selectedDate,
    selectedMember,
    selectedOffice,
    selectedTimezone,
    getDailyTimesheets,
  ]);

  useEffect(() => {
    if (queryParamMember && orgMembersShortList?.length > 0) {
      const member = orgMembersShortList.find(m => m.id === queryParamMember);
      if (member) {
        setSelectedMember(member);
        setSelectedTask("");
      }
    }
  }, [orgMembersShortList]);

  useEffect(() => {
    if (queryParamOffice && officesShortList?.length > 0) {
      const office = officesShortList.find(f => f.id === queryParamOffice);
      if (office) {
        setSelectedOffice({ label: office.name, value: office.id });
        getOfficeDetails({
          organization_id: selectedOrganization.id,
          office_id: queryParamOffice,
        });
      }
    }
  }, [officesShortList, selectedOrganization]);

  useEffect(() => {
    if (timesheet && timesheet.length > 0) {
      let list = [];
      let timeDurations = [];
      timesheet.map(time => {
        let timeSlot = {};

        let compareTime = moment("00:00", "HH:mm");

        let startTime = time.start_timestamp.format("HH:mm");

        timeSlot.id = time.id;
        timeSlot.start_time = time.start_timestamp;
        timeSlot.end_time = time.end_timestamp;
        timeSlot.duration = time.duration;
        timeSlot.work_order = time.work_order;
        timeSlot.job = time.job;
        // timeDurations.push(moment(toHHMMSS(duration)).format("HH:mm:ss"));
        timeDurations.push(time.duration);

        let startTimeDuration = moment.utc(
          moment(startTime, "HH:mm").diff(moment(compareTime)),
        );
        let startTimeHour = parseInt(
          moment.duration(startTimeDuration).asHours(),
        );
        let startTimeMinutes = parseInt(
          moment.duration(startTimeDuration).asMinutes() % 60,
        );

        timeSlot.top = parseInt((startTimeHour + startTimeMinutes / 60) * 75);

        let hours = parseInt(toHHMMSS(time.duration).split(":")[0]);
        let minutes = parseInt(toHHMMSS(time.duration).split(":")[1]);
        if (parseInt((hours + minutes / 60) * 75) === 0) {
          timeSlot.height = 1;
        } else {
          timeSlot.height = parseInt((hours + minutes / 60) * 75);
        }
        list.push(timeSlot);
      });
      setTimesheetList(list);
      setDurations(timeDurations);
    } else if (timesheet && timesheet.length === 0) {
      setTimesheetList([]);
      setDurations([]);
    }
  }, [timesheet]);

  // const selectProject = e => {
  //   let organizationId = selectedOrganization ? selectedOrganization.id : '';
  //   if (organizationId) {
  //     const payload = {
  //       organization_id: organizationId,
  //       date: selectedDate,
  //     };
  //     if (selectedMember && e.value) {
  //       payload.user_id = selectedMember.id;
  //     }
  //     if (e && e.value) {
  //       payload.project_id = e.value;
  //     }
  //     if (selectedTimezone && selectedTimezone.timezone) {
  //       payload.timezone = selectedTimezone.timezone;
  //     }
  //     getDailyTimesheets(payload);
  //   }

  //   if (
  //     e &&
  //     e.value &&
  //     selectedProject &&
  //     selectedProject.value &&
  //     e.value !== selectedProject.value
  //   ) {
  //     setSelectedProject(e);
  //     setSelectedTask('');
  //   } else if ((e && !e.value) || e == null) {
  //     setSelectedMember('');
  //     setSelectedTask('');
  //     setSelectedProject(e);
  //   } else {
  //     setSelectedProject(e);
  //   }
  // };
  // const selectTask = e => {
  //   let organizationId = selectedOrganization ? selectedOrganization.id : '';
  //   if (organizationId) {
  //     const payload = {
  //       organization_id: organizationId,
  //       date: selectedDate,
  //     };
  //     if (selectedMember) {
  //       payload.user_id = selectedMember.id;
  //     }
  //     if (e && e.value) {
  //       payload.task_id = e.value;
  //     }
  //     if (selectedTimezone && selectedTimezone.timezone) {
  //       payload.timezone = selectedTimezone.timezone;
  //     }
  //     getDailyTimesheets(payload);
  //   }
  //   setSelectedTask(e);
  // };

  const selectTeamMember = e => {
    let organizationId = selectedOrganization ? selectedOrganization.id : "";
    if (organizationId) {
      const payload = {
        organization_id: organizationId,
        date: selectedDate,
        user_id: e.id,
      };
      // if (selectedProject && selectedProject.value) {
      //   payload.project_id = selectedProject.value;
      // }
      // if (selectedTask && selectedTask.value) {
      //   payload.task_id = selectedTask.value;
      // }
      // if (
      //   selectedTimezone &&
      //   selectedTimezone.type === 'Member' &&
      //   e.timezone &&
      //   e.timezone.tz
      // ) {
      //   payload.timezone = e.timezone.tz;
      //   setSelectedTimezone({
      //     type: 'Member',
      //     timezone: e.timezone.tz,
      //     offset: e.timezone.offset,
      //   });
      // } else if (
      //   selectedTimezone &&
      //   selectedTimezone.type === 'Organization' &&
      //   selectedTimezone.timezone
      // ) {
      //   payload.timezone = selectedTimezone.timezone;
      // }
      setSelectedMember(e);
      getDailyTimesheets(payload);
    }
  };

  const clearMember = () => {
    let organizationId = selectedOrganization ? selectedOrganization.id : "";
    if (organizationId) {
      const payload = {
        organization_id: organizationId,
        date: selectedDate,
      };
      // if (selectedProject && selectedProject.value) {
      //   payload.project_id = selectedProject.value;
      // }
      // if (selectedTask && selectedTask.value) {
      //   payload.task_id = selectedTask.value;
      // }
      // if (selectedOrganization.timezone) {
      //   setSelectedTimezone({
      //     type: 'Organization',
      //     timezone: selectedOrganization.timezone,
      //     offset: selectedOrganization.timezoneoffset,
      //   });
      //   payload.timezone = selectedOrganization.timezone;
      // }
      getDailyTimesheets(payload);
    }
    setSelectedMember("");
  };

  const singleDateChangeDaily = direction => {
    const newDate =
      direction === "right"
        ? moment(selectedDate).add(1, "days")
        : moment(selectedDate).subtract(1, "days");
    selectDate(newDate);
  };

  const selectDate = date => {
    let organizationId = selectedOrganization ? selectedOrganization.id : "";
    setSelectedDate(date.format("YYYY-MM-DD"));
    if (organizationId) {
      const payload = {
        organization_id: organizationId,
        date: date.format("YYYY-MM-DD"),
      };
      // if (selectedProject && selectedProject.value) {
      //   payload.project_id = selectedProject.value;
      // }
      // if (selectedTask && selectedTask.value) {
      //   payload.task_id = selectedTask.value;
      // }
      if (selectedMember && selectedMember.id !== "") {
        payload.user_id = selectedMember.id;
      }
      if (selectedTimezone && selectedTimezone.timezone) {
        payload.timezone = selectedTimezone.timezone;
      }
      getDailyTimesheets(payload);
    }
  };

  const selectOffice = e => {
    if (e && e.value && selectedOrganization && selectedOrganization.id) {
      const payload = {
        organization_id: selectedOrganization.id,
        office_id: e.value,
      };

      getOfficeDetails(payload);
    }

    setSelectedMember("");
    // getDailyTimesheets(payload);
    setSelectedOffice(e);
  };

  const onHoverChange = index => {
    setSelectIndex(index);
  };

  const getTimesheetList = () => {
    setTimeout(() => {
      let organizationId = selectedOrganization ? selectedOrganization.id : "";
      if (organizationId) {
        const payload = {
          organization_id: organizationId,
          date: selectedDate,
        };
        if (selectedMember) {
          payload.user_id = selectedMember.id;
        }
        // if (selectedTimezone && selectedTimezone.timezone) {
        //   payload.timezone = selectedTimezone.timezone;
        // }
        getDailyTimesheets(payload);
      }
    }, 1000);
  };

  const filteredTimeList = useMemo(
    () =>
      timesheetList?.length
        ? timeList.filter(
            (time, index) =>
              index > moment(timesheetList[0].start_time).hours() - 1 &&
              index <
                moment(
                  timesheetList[timesheetList.length - 1].end_time,
                ).hours() +
                  2,
          )
        : [],
    [timesheetList],
  );

  const handleAddTimesheet = payload => {
    createDailyTimesheets(payload);
  };

  useEffect(() => {
    if (manualTimeAdded) {
      setManualTimeModalVisible(false);
      const formattedTime = moment(
        manualTimeDetails.start_timestamp * 1000,
      ).format("YYYY-MM-DD");
      setSelectedDate(formattedTime);
      setSelectedMember(manualTimeDetails.user);

      // getTimesheetList();
      setTimeout(() => {
        clearDailyTimesheetAdded();
      }, 500);
    }
  }, [manualTimeAdded, manualTimeDetails]);

  useEffect(() => {
    if (manualTimeAdded) {
      getTimesheetList();
      setTimeout(() => {
        clearDailyTimesheetAdded();
      }, 500);
    }
  }, [selectedDate, selectedMember]);

  // inside your component
  const throttledDeleteTimesheet = useDebouncedCallback(
    (id, startTimestamp, endTimestamp, reason) => {
      if (selectedOrganization && selectedOrganization.id) {
        const userId = localStorage.getItem("user_id");
        const payload = {
          organization_id: selectedOrganization.id,
          timesheet_id: id,
          start: moment(startTimestamp).utc().unix(),
          end: moment(endTimestamp).utc().unix(),
          comment: reason,
        };

        if (
          selectedOrganization.role &&
          selectedOrganization.role !== "member" &&
          selectedMember &&
          selectedMember.id &&
          userId &&
          userId !== selectedMember.id
        ) {
          payload.user_id = selectedMember.id;
        }

        deleteDailyTimesheets(payload);
      }

      setTimeout(() => {
        setDeleteTimesheetModalVisible(false);
        getTimesheetList();
      }, 800);
    },
    500,
    { leading: true, trailing: false },
  );

  const handleDeleteTimesheet = (id, startTimestamp, endTimestamp, reason) => {
    throttledDeleteTimesheet(id, startTimestamp, endTimestamp, reason);
  };

  const toggleManualTimeModal = () => {
    setManualTimeModalVisible(!manualTimeModalVisible);
  };

  const toggleDeleteTimeModal = timesheet => {
    if (!deleteTimesheetModalVisible) {
      setCurrentTimesheet(timesheet);
    } else {
      setCurrentTimesheet(null);
    }
    setDeleteTimesheetModalVisible(!deleteTimesheetModalVisible);
  };

  const toggleInfoPopup = index => {
    setInfoPopup(index);
  };

  const selectToday = () => {
    const date = moment();
    setIsTodaySelected(true);
    selectDate(date);
  };
  const renderTimesheetContent = () => {
    if (isLoading) return <ComponentCircleLoader />;
    if (!timesheetList?.length)
      return <NoDataComponent title="No Timesheet Records Found." />;

    return (
      <TimeSlotContent
        timesheetList={timesheetList}
        filteredTimeList={filteredTimeList}
        infoPopup={infoPopup}
        setInfoPopup={setInfoPopup}
        durations={durations}
        toggleInfoPopup={toggleInfoPopup}
        selectIndex={selectIndex}
        // idleTimePopup={idleTimePopup}
        // setIdleTimePopup={setIdleTimePopup}
        // toggleIdleTimePopup={toggleIdleTimePopup}
        onHoverChange={onHoverChange}
        toggleManualTimeModal={toggleManualTimeModal}
        toggleDeleteTimeModal={toggleDeleteTimeModal}
        // toggleIdleTimeModal={toggleIdleTimeModal}
        // totalIdleTimesheet={totalIdleTimesheet}
        selectedOrganization={selectedOrganization}
      />
    );
  };

  return (
    <div className="content">
      <ManualTimeModal
        isOpen={manualTimeModalVisible}
        toggle={toggleManualTimeModal}
        manualTimeAdded={manualTimeAdded}
        selectedOrganization={selectedOrganization}
        getProjectAssigneeList={getProjectAssigneeList}
        projectAssigneeList={projectAssigneeList}
        getOrganizationMembersList={getOrganizationMembersShortList}
        organizationMembersList={orgMembersShortList}
        membersList={membersList}
        handleAddTimesheet={handleAddTimesheet}
        isLoading={manualTimeLoading}
        filterSelectedDate={selectedDate}
        filterSelectedMember={selectedMember}
      />
      <DeleteTimeModal
        isOpen={deleteTimesheetModalVisible}
        toggle={toggleDeleteTimeModal}
        currentTimesheet={currentTimesheet}
        handleDeleteTimesheet={handleDeleteTimesheet}
        isLoading={deleteTimesheetLoading}
        timezone={selectedTimezone && selectedTimezone.timezone}
      />
      {/* <ContainerNew> */}
        <CommonGrid alignItem="center">
          <PageTitle>Daily Timesheet</PageTitle>
          {(selectedOrganization &&
            selectedOrganization.configuration &&
            selectedOrganization.configuration
              .allow_user_add_manual_timesheet) ||
          (selectedOrganization &&
            selectedOrganization.role &&
            selectedOrganization.role !== "member") ? (
            <CommonGrid>
              <ColoredButtonWithIcon
                style={{ alignSelf: "flex-end" }}
                onClick={() => toggleManualTimeModal()}
              >
                <ButtonIcon src={Plus} alt="" />
                Add Manual Time
              </ColoredButtonWithIcon>
            </CommonGrid>
          ) : null}
        </CommonGrid>
        <StickyFilterContainer paddingBottom="5px" showShadow={showShadow}>
          <TimesheetFilter
            page="daily"
            selectedDate={moment(selectedDate)}
            selectToday={selectToday}
            onDateChange={selectDate}
            isTodaySelected={isTodaySelected}
            setIsTodaySelected={setIsTodaySelected}
            selectedProject={selectedProject}
            // selectProject={selectProject}
            selectedTask={selectedTask}
            // selectTask={selectTask}
            selectedMember={selectedMember}
            selectMember={selectTeamMember}
            clearMember={clearMember}
            getOfficesShortList={getOfficesShortList}
            officesShortList={officesShortList}
            selectedOffice={selectedOffice}
            selectOffice={selectOffice}
            getOrganizationMembersList={getOrganizationMembersShortList}
            organizationMembersList={orgMembersShortList}
            selectedOrganization={selectedOrganization}
            // selectTimezone={onTimezoneSelect}
            selectedTimezone={selectedTimezone}
            officeDetails={officeDetails}
            isYesterday={isYesterday}
            singleDateChangeDaily={singleDateChangeDaily}
            showShadow={showShadow}
          />
        </StickyFilterContainer>
        <Container style={{ padding: `30px` }}>
          {renderTimesheetContent()}
        </Container>
      {/* </ContainerNew> */}
    </div>
  );
};

export default Daily;
